import { getRateInQuoteWithAssetDecimals } from "../api/accountant";
import type { ChainId } from "../api/vault-config";
import { bigIntToNumberAsString } from "../utils/bigint";
import { getVaultByKey } from "../vaults";
import type { VaultKey } from "../vaults/config";

interface GetDepositExchangeRateParams {
  vaultKey: VaultKey;
  sourceChainId: ChainId;
  depositTokenSymbol: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

/**
 * Gets the deposit exchange rate for a vault (tokens per share)
 * @throws {Error} If the token address is not found or if there's an error fetching the rate
 * @returns Promise<string> The exchange rate as a formatted string representing how many deposit tokens are worth 1 share
 */
const getDepositExchangeRate = async ({
  vaultKey,
  sourceChainId,
  depositTokenSymbol,
  minimumFractionDigits,
  maximumFractionDigits = 3,
}: GetDepositExchangeRateParams) => {
  const vault = await getVaultByKey(vaultKey);

  const sourceChain = vault.deposit.sourceChains[sourceChainId];
  if (!sourceChain) {
    throw new Error(
      `Source chain ${sourceChainId} not found for vault ${vaultKey}`
    );
  }

  const depositToken = sourceChain.depositTokens?.[depositTokenSymbol];
  if (!depositToken) {
    throw new Error(
      `Deposit token ${depositTokenSymbol} not found for chain ${sourceChainId}`
    );
  }

  const tokenAddress = depositToken.address;
  if (!tokenAddress) {
    throw new Error(`Token address not found for ${depositTokenSymbol}`);
  }

  const [decimals, rate] = await getRateInQuoteWithAssetDecimals({
    assetAddress: tokenAddress,
    accountantAddress: vault.contracts.accountant,
    chainId: sourceChainId,
  });

  if (rate.status === "failure") {
    throw new Error(`Failed to get rate: ${rate.error.message}`);
  }
  if (decimals.status === "failure") {
    throw new Error(`Failed to get decimals: ${decimals.error.message}`);
  }

  return bigIntToNumberAsString(rate.result, {
    decimals: decimals.result,
    minimumFractionDigits,
    maximumFractionDigits,
  });
};

interface GetWithdrawExchangeRateParams {
  vaultKey: VaultKey;
  sourceChainId: ChainId;
  destinationChainId: ChainId;
  wantTokenSymbol: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

/**
 * Gets the withdraw exchange rate for a vault (tokens per share)
 * @throws {Error} If the token address is not found or if there's an error fetching the rate
 * @returns Promise<string> The exchange rate as a formatted string representing how many want tokens are worth 1 share
 */
const getWithdrawExchangeRate = async ({
  vaultKey,
  sourceChainId,
  destinationChainId,
  wantTokenSymbol,
  minimumFractionDigits,
  maximumFractionDigits = 3,
}: GetWithdrawExchangeRateParams) => {
  const vault = await getVaultByKey(vaultKey);

  const sourceChain = vault.withdraw.sourceChains[sourceChainId];
  if (!sourceChain) {
    throw new Error(
      `Source chain ${sourceChainId} not found for vault ${vaultKey}`
    );
  }

  const destinationChain = sourceChain.destinationChains[destinationChainId];
  if (!destinationChain) {
    throw new Error(
      `Destination chain ${destinationChainId} not found for source chain ${sourceChainId}`
    );
  }

  const wantToken = destinationChain.wantTokens?.[wantTokenSymbol];
  if (!wantToken) {
    throw new Error(
      `Want token ${wantTokenSymbol} not found for chain ${destinationChainId}`
    );
  }

  const tokenAddress = wantToken.address;
  if (!tokenAddress) {
    throw new Error(`Token address not found for ${wantTokenSymbol}`);
  }

  const [decimals, rate] = await getRateInQuoteWithAssetDecimals({
    assetAddress: tokenAddress,
    accountantAddress: vault.contracts.accountant,
    chainId: sourceChainId,
  });

  if (rate.status === "failure") {
    throw new Error(`Failed to get rate: ${rate.error.message}`);
  }
  if (decimals.status === "failure") {
    throw new Error(`Failed to get decimals: ${decimals.error.message}`);
  }

  return bigIntToNumberAsString(rate.result, {
    decimals: decimals.result,
    minimumFractionDigits,
    maximumFractionDigits,
  });
};

export { getDepositExchangeRate, getWithdrawExchangeRate };
